import type { ICompetenceDisplayItem } from '@/core/competences/competenceDisplay.types';
import type { Components } from '@/api/generated/api';
import type { ISelectedCompetence } from '@/interfaces';
import { EIcon } from 'common/enum/misc.enum';

type TCompetenceDisplaySource = Pick<Components.Schemas.CompetenceDto, 'description' | 'domainId' | 'id'>;
type TCompetenceDomainLookup = Readonly<
  Partial<Record<number, Pick<Components.Schemas.CompetenceDomainDto, 'symbol'>>>
>;
type TSchoolSubjectColorResolver = (schoolSubjectId: number) => string | undefined;

export const toCompetenceDisplayItems = (
  competences: readonly TCompetenceDisplaySource[],
  competenceDomainById: TCompetenceDomainLookup,
  domainColor?: string,
): ICompetenceDisplayItem[] =>
  uniqueCompetenceDisplayItems(
    competences.map((competence) => ({
      description: competence.description,
      domainColor,
      domainSymbol: competenceDomainById[competence.domainId]?.symbol ?? EIcon.COMPETENCE,
      id: competence.id,
    })),
  );

export const toSelectedCompetenceDisplayItem = (
  { competence, domain }: ISelectedCompetence,
  getSchoolSubjectColor: TSchoolSubjectColorResolver,
): ICompetenceDisplayItem => ({
  description: competence.description,
  domainColor: domain.schoolSubjectId == null ? undefined : getSchoolSubjectColor(domain.schoolSubjectId),
  domainSymbol: domain.symbol,
  id: competence.id,
});

export const toSelectedCompetenceDisplayItems = (
  selectedCompetences: readonly ISelectedCompetence[],
  getSchoolSubjectColor: TSchoolSubjectColorResolver,
): ICompetenceDisplayItem[] =>
  uniqueCompetenceDisplayItems(
    selectedCompetences.map((selectedCompetence) =>
      toSelectedCompetenceDisplayItem(selectedCompetence, getSchoolSubjectColor),
    ),
  );

export const uniqueCompetenceDisplayItems = (items: readonly ICompetenceDisplayItem[]): ICompetenceDisplayItem[] => {
  const itemsById = new Map<number, ICompetenceDisplayItem>();
  for (const item of items) {
    if (!itemsById.has(item.id)) itemsById.set(item.id, item);
  }
  return [...itemsById.values()];
};
